import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, CircularProgress,
    Switch, FormControlLabel, Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { supplierAPI } from '../../services/api';

const INITIAL_FORM = {
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    isActive: true,
};

const SupplierFormDialog = ({ open, onClose, onSuccess, supplier }) => {
    const { t } = useTranslation();
    const isEdit = Boolean(supplier);

    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            if (supplier) {
                setForm({
                    name: supplier.name || '',
                    phone: supplier.phone || '',
                    email: supplier.email || '',
                    address: supplier.address || '',
                    notes: supplier.notes || '',
                    isActive: supplier.isActive !== false,
                });
            } else {
                setForm(INITIAL_FORM);
            }
            setErrors({});
        }
    }, [open, supplier]);

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = t('suppliers.nameRequired');
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errs.email = t('validation.email');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim().toLowerCase(),
                address: form.address.trim(),
                notes: form.notes.trim(),
                isActive: form.isActive,
            };

            if (isEdit) {
                await supplierAPI.update(supplier._id, payload);
                toast.success(t('suppliers.updateSuccess'));
            } else {
                await supplierAPI.create(payload);
                toast.success(t('suppliers.createSuccess'));
            }
            onSuccess();
        } catch (err) {
            const msg = err.response?.data?.message || t('suppliers.saveError');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {isEdit ? t('suppliers.editTitle') : t('suppliers.addTitle')}
            </DialogTitle>

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>
                    <TextField
                        label={t('suppliers.name')}
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        required
                        fullWidth
                        size="small"
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('suppliers.phone')}
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('suppliers.email')}
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                error={Boolean(errors.email)}
                                helperText={errors.email}
                                fullWidth
                                size="small"
                                type="email"
                            />
                        </Grid>
                    </Grid>

                    <TextField
                        label={t('suppliers.address')}
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                    />

                    <TextField
                        label={t('suppliers.notes')}
                        name="notes"
                        value={form.notes}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.isActive}
                                onChange={handleChange}
                                name="isActive"
                                color="primary"
                            />
                        }
                        label={t('suppliers.isActive')}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? t('common.processing') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SupplierFormDialog;